import type { GeoPointFeature } from '../../../types'

/**
 * Geração automática de análises (fluxo de "micronutrientes").
 *
 * Atalho da versão em produção (`Components/googleMaps/index.tsx:280-486`): ao
 * concluir uma seleção de pontos o usuário pode pedir que o sistema crie sozinho
 * as análises complementares, em vez de repetir a seleção 3 vezes.
 *
 * Os cods abaixo são os mesmos da produção — portados como estão. Não são
 * configuráveis por laboratório: se um laboratório usar outra numeração, a
 * análise simplesmente não é encontrada e o usuário é avisado (ver
 * `montarAnalisesAutomaticas`).
 */

//#region CODS (hardcoded, iguais aos da produção)
/** Profundidades no cadastro do laboratório. */
const PROFUNDIDADE = {
    /** 00-20 */
    P_00_20: 1,
    /** 20-40 */
    P_20_40: 2,
    /** 40-60 */
    P_40_60: 3,
    /** 00-50 */
    P_00_50: 4,
} as const

/** Analitos dentro de `analises.analises[]`. */
const ANALITO = {
    MACRO: 1,
    MICRO: 2,
    ENXOFRE: 3,
    GRANULOMETRIA: 5,
} as const
//#endregion

/** Item do grupo de análises do laboratório (retorno de `getLabAnalise`). */
interface AnaliseDoLab {
    cod_analise?: number
    cod_profundidade?: number
    analise_nome?: string
    profundidade_nome?: string
    valor?: string | number
    cor?: string
    analises?: {
        profundidade?: { nome?: string; cod?: number }
        analises?: { cod?: number; nome?: string }[]
    }
    [key: string]: any
}

/** `true` se a análise contém o analito informado. */
function temAnalito(analise: AnaliseDoLab, cod: number): boolean {
    return (analise?.analises?.analises ?? []).some((item) => item?.cod === cod)
}

/** Opções que o usuário marca no modal "Deseja gerar as seguintes análises?". */
export type OpcaoAutoAnalise = 'macro_enxofre_40_60' | 'granulometria_00_50' | 'macro_restante_00_20'

export const OPCOES_AUTO_ANALISE: { valor: OpcaoAutoAnalise; rotulo: string }[] = [
    { valor: 'macro_enxofre_40_60', rotulo: '40 - 60 | Macro + Enxofre' },
    { valor: 'granulometria_00_50', rotulo: '00 - 50 | Granulometria' },
]

/** Localiza no laboratório a análise correspondente a cada opção. */
function localizarAnalise(
    opcao: OpcaoAutoAnalise,
    grupo: AnaliseDoLab[]
): AnaliseDoLab | undefined {
    const lista = grupo ?? []

    if (opcao === 'macro_enxofre_40_60') {
        return lista.find(
            (item) =>
                item?.cod_profundidade === PROFUNDIDADE.P_40_60 &&
                temAnalito(item, ANALITO.ENXOFRE) &&
                temAnalito(item, ANALITO.MACRO)
        )
    }

    if (opcao === 'granulometria_00_50') {
        return lista.find(
            (item) =>
                item?.cod_profundidade === PROFUNDIDADE.P_00_50 &&
                temAnalito(item, ANALITO.GRANULOMETRIA)
        )
    }

    // 00-20 Macro+S SEM micronutrientes — é o que sobra dos pontos que não
    // receberam micro.
    return lista.find(
        (item) =>
            item?.cod_profundidade === PROFUNDIDADE.P_00_20 &&
            temAnalito(item, ANALITO.ENXOFRE) &&
            temAnalito(item, ANALITO.MACRO) &&
            !temAnalito(item, ANALITO.MICRO)
    )
}

/**
 * A análise de MICRONUTRIENTES (00-20 com Macro + Micro).
 *
 * É ela que o select passa a exibir quando o usuário responde "Sim" a "Deseja
 * Gerar Micronutrientes?" — assim os pontos da 2ª passada já saem com a cor
 * correta. Mesma busca da produção (`googleMaps/index.tsx:304-309`).
 */
export function localizarAnaliseMicro(grupo: AnaliseDoLab[]): AnaliseDoLab | undefined {
    return (grupo ?? []).find(
        (item) =>
            item?.cod_profundidade === PROFUNDIDADE.P_00_20 &&
            temAnalito(item, ANALITO.MACRO) &&
            temAnalito(item, ANALITO.MICRO)
    )
}

/** A análise 20-40 Macro+S, cuja cor identifica os pontos "sem micro" na malha. */
export function localizarAnalise2040(grupo: AnaliseDoLab[]): AnaliseDoLab | undefined {
    return (grupo ?? []).find(
        (item) =>
            item?.cod_profundidade === PROFUNDIDADE.P_20_40 &&
            temAnalito(item, ANALITO.ENXOFRE) &&
            temAnalito(item, ANALITO.MACRO)
    )
}

export interface AnaliseAutoMontada {
    analise_nome: string
    profundidade: string
    cod_analise: number
    cor: string
    valor: string
    pontos: GeoPointFeature[]
}

export interface ResultadoAutoAnalise {
    /** Análises prontas para virar cards. */
    montadas: AnaliseAutoMontada[]
    /** Rótulos das opções que o laboratório não oferece. */
    nao_encontradas: string[]
}

/**
 * Monta as análises automáticas escolhidas pelo usuário.
 *
 * A lista é montada por INTEIRO antes de qualquer gravação: se uma análise não
 * existe no laboratório, ela entra em `nao_encontradas` e as demais seguem
 * normalmente. A produção acessa `analise.profundidade_nome` sem guarda e
 * estoura nesse caso — aqui o usuário é avisado e o que dá para gerar é gerado.
 *
 * @param opcoes o que o usuário marcou
 * @param grupo grupo de análises do laboratório
 * @param pontos_com_micro pontos que receberam micronutrientes
 * @param pontos_restantes pontos sem micro (só para `macro_restante_00_20`)
 */
export function montarAnalisesAutomaticas(
    opcoes: OpcaoAutoAnalise[],
    grupo: AnaliseDoLab[],
    pontos_com_micro: GeoPointFeature[],
    pontos_restantes: GeoPointFeature[] = []
): ResultadoAutoAnalise {
    const montadas: AnaliseAutoMontada[] = []
    const nao_encontradas: string[] = []

    ;(opcoes ?? []).forEach((opcao) => {
        const analise = localizarAnalise(opcao, grupo)

        if (!analise) {
            const rotulo =
                OPCOES_AUTO_ANALISE.find((item) => item.valor === opcao)?.rotulo ??
                '00 - 20 | Macro + S (restante)'
            nao_encontradas.push(rotulo)
            return
        }

        // O "restante" usa os pontos sem micro; as demais repetem a seleção.
        const pontos = opcao === 'macro_restante_00_20' ? pontos_restantes : pontos_com_micro

        if (pontos.length === 0) return

        montadas.push({
            analise_nome: analise?.analise_nome ?? '',
            profundidade: analise?.analises?.profundidade?.nome ?? analise?.profundidade_nome ?? '',
            cod_analise: analise?.cod_analise,
            cor: analise?.cor ?? '#ffffff',
            valor: String(analise?.valor ?? 0),
            pontos,
        })
    })

    return { montadas, nao_encontradas }
}

/**
 * Pontos que NÃO receberam micronutrientes: os que continuam livres na malha ou
 * pertencem à análise 20-40. Mesma regra do ramo '6' da produção
 * (`googleMaps/index.tsx:432-440`), que filtra por cor.
 */
export function obterPontosRestantes(
    malha: GeoPointFeature[],
    cor_20_40?: string
): GeoPointFeature[] {
    return (malha ?? []).filter((ponto: any) => {
        const cor = ponto?.COR
        return !cor || cor === '#ffffff' || (cor_20_40 && cor === cor_20_40)
    })
}
